import {ClerkProvider} from '@clerk/clerk-react'
import {captureException} from '@sentry/react'
import {
	MutationCache,
	QueryClient,
	QueryClientProvider,
} from '@tanstack/react-query'
import {createRouter, RouterProvider} from '@tanstack/react-router'
import {toast} from 'sonner'
import {ApiError} from '@/lib/api/client'
import {AuthWrapper} from './components/AuthWrapper'
import {Toaster} from './components/ui/sonner'
import {TooltipProvider} from './components/ui/tooltip'
import {ModalProvider} from './providers/modal-provider'
import {ThemeProvider} from './providers/theme-provider'

// Import the generated route tree
import {routeTree} from './routeTree.gen'

import './index.css'

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

// Global mutation error handler via MutationCache
// This ensures all mutation errors are captured in Sentry and shown to users
const mutationCache = new MutationCache({
	onError: (error, _variables, _context, mutation) => {
		// Send all mutation errors to Sentry with mutation context
		captureException(error, {
			extra: {
				mutationKey: mutation.options.mutationKey,
			},
		})

		// Show a toast unless the mutation opts out with meta.skipToast.
		// Prefer the server's message for client errors (4xx) — these are
		// user-actionable (e.g. a validation error like a missing required note).
		// For 5xx / non-ApiError throws (internal failure, network drop), fall
		// back to the mutation's generic meta.errorMessage so we don't surface
		// internal details, then to a last-resort generic string.
		if (!mutation.options.meta?.skipToast) {
			const clientErrorMessage =
				error instanceof ApiError && error.status < 500 ? error.message : null
			const errorMessage =
				clientErrorMessage ||
				(mutation.options.meta?.errorMessage as string) ||
				'An error occurred'
			toast.error(errorMessage)
		}
	},
})

// Create a QueryClient instance with global mutation cache
const queryClient = new QueryClient({
	mutationCache,
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // 5 minutes
			// Retry transient failures (network, 5xx) up to 3 times, but never 4xx:
			// a client error won't heal on retry — a 401 in particular re-sends the
			// same expired token and multiplies an auth blip into a request storm.
			// 408 (timeout) and 429 (rate limit) are the transient exceptions.
			retry: (failureCount, error) => {
				if (
					error instanceof ApiError &&
					error.status >= 400 &&
					error.status < 500 &&
					error.status !== 408 &&
					error.status !== 429
				) {
					return false
				}
				return failureCount < 3
			},
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
			refetchOnWindowFocus: false, // Don't refetch when switching tabs
			refetchOnReconnect: true, // Refetch when internet reconnects
			refetchOnMount: true, // Refetch stale data when component mounts
		},
	},
})

// Create the router using the generated route tree
const router = createRouter({
	routeTree,
	defaultPreload: 'intent',
	defaultPreloadStaleTime: 0,
	// Pages scroll in their own overflow-y-auto containers (not window), so
	// restoration tracks scrolled elements per location. Containers that must
	// survive remounts get a stable data-scroll-restoration-id (see TodayPage).
	scrollRestoration: true,
	context: {queryClient},
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

export function App() {
	if (!CLERK_PUBLISHABLE_KEY) {
		throw new Error('Missing Clerk Publishable Key')
	}

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider>
				<ClerkProvider
					publishableKey={CLERK_PUBLISHABLE_KEY}
					allowedRedirectOrigins={[
						'http://localhost:3000',
						'http://localhost:5173',
						import.meta.env.VITE_APP_URL || '',
					].filter(Boolean)}
					signInForceRedirectUrl='/today'
					signUpForceRedirectUrl='/auth/callback'
					signInFallbackRedirectUrl='/today'
					signUpFallbackRedirectUrl='/auth/callback'
					localization={{
						signIn: {
							start: {
								title: '',
								subtitle: '',
							},
						},
					}}
				>
					<AuthWrapper>
						<TooltipProvider>
							<ModalProvider>
								<RouterProvider router={router} />
								<Toaster position='bottom-right' closeButton />
							</ModalProvider>
						</TooltipProvider>
					</AuthWrapper>
				</ClerkProvider>
			</ThemeProvider>
		</QueryClientProvider>
	)
}

export default App
